/** Horizon scrolls `.page-wrapper` on desktop and the window on mobile. */
function scrollTop() {
  const wrapper = document.querySelector('.page-wrapper');
  return Math.max(window.scrollY, wrapper?.scrollTop ?? 0);
}

class SkyHeader extends HTMLElement {
  connectedCallback() {
    this.drawer = this.querySelector('.sky-menu-drawer');
    this.aside = this.querySelector('.sky-menu-drawer__aside');
    this.openButton = this.querySelector('[data-sky-menu-open]');

    this.onScroll = () => this.classList.toggle('is-scrolled', scrollTop() > 10);
    this.onKeydown = (event) => {
      if (event.key === 'Escape' && this.drawer.classList.contains('is-open')) this.close();
    };

    document.addEventListener('scroll', this.onScroll, { passive: true, capture: true });
    document.addEventListener('keydown', this.onKeydown);
    this.onScroll();

    this.openButton?.addEventListener('click', () => this.open());
    this.querySelectorAll('[data-sky-menu-close]').forEach((el) => el.addEventListener('click', () => this.close()));
    this.querySelectorAll('[data-sky-submenu]').forEach((button) =>
      button.addEventListener('click', () => this.showSubmenu(button))
    );
    this.querySelectorAll('[data-sky-submenu-back]').forEach((button) =>
      button.addEventListener('click', () => this.showSubmenu(null))
    );

    // Theme editor: keep the drawer out of the way when the section reloads
    document.addEventListener('shopify:section:unload', () => this.unlock());
  }

  disconnectedCallback() {
    document.removeEventListener('scroll', this.onScroll, { capture: true });
    document.removeEventListener('keydown', this.onKeydown);
    this.unlock();
  }

  open() {
    this.drawer.hidden = false;
    document.documentElement.setAttribute('scroll-lock', '');
    this.openButton?.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.drawer.classList.add('is-open');
        this.drawer.querySelector('.sky-menu-drawer__panel')?.focus({ preventScroll: true });
      });
    });
  }

  close() {
    this.drawer.classList.remove('is-open');
    this.openButton?.setAttribute('aria-expanded', 'false');
    const panel = this.drawer.querySelector('.sky-menu-drawer__panel');
    const done = () => {
      if (this.drawer.classList.contains('is-open')) return;
      this.drawer.hidden = true;
      this.showSubmenu(null);
      this.unlock();
    };
    panel?.addEventListener('transitionend', done, { once: true });
    setTimeout(done, 700);
    this.openButton?.focus({ preventScroll: true });
  }

  unlock() {
    document.documentElement.removeAttribute('scroll-lock');
  }

  /** @param {HTMLElement | null} button */
  showSubmenu(button) {
    const targetId = button?.dataset.skySubmenu;
    const views = this.aside.querySelectorAll('.sky-menu-drawer__aside-view');
    let shown = false;

    views.forEach((view) => {
      const active = targetId ? view.id === targetId : view.hasAttribute('data-sky-aside-default');
      view.classList.toggle('is-active', active);
      if (active && targetId) shown = true;
    });

    this.querySelectorAll('[data-sky-submenu]').forEach((b) =>
      b.setAttribute('aria-expanded', String(b === button && shown))
    );
    this.aside.classList.toggle('is-mobile-open', shown);
  }
}

if (!customElements.get('sky-header')) customElements.define('sky-header', SkyHeader);

/**
 * "Typing" placeholder (Sky): the text stays ~2s, is erased letter by letter, then typed back, on a loop.
 * For inputs with `data-running-placeholder`; pauses while the field is focused or filled, or off screen.
 * @param {HTMLInputElement} input
 */
function runPlaceholder(input) {
  const text = input.getAttribute('placeholder') || '';
  if (!text || input.dataset.runningReady) return;
  input.dataset.runningReady = 'true';

  const HOLD = 2100;
  const ERASE = 30;
  const TYPE = 45;
  let length = text.length;
  let direction = -1;

  const idle = () => document.activeElement === input || input.value !== '' || input.offsetParent === null;

  const tick = () => {
    if (idle()) {
      input.placeholder = text;
      length = text.length;
      direction = -1;
      setTimeout(tick, HOLD);
      return;
    }
    length += direction;
    input.placeholder = text.slice(0, Math.max(0, length));
    if (length <= 0) {
      direction = 1;
      setTimeout(tick, 120);
    } else if (length >= text.length) {
      direction = -1;
      setTimeout(tick, HOLD);
    } else {
      setTimeout(tick, direction < 0 ? ERASE : TYPE);
    }
  };
  setTimeout(tick, HOLD);
}

if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
  document.querySelectorAll('input[data-running-placeholder]').forEach((input) => runPlaceholder(/** @type {HTMLInputElement} */ (input)));
}

/**
 * Account popover, loaded on intent: signed-out visitors get a plain icon button; the first mouse hover,
 * focus or tap swaps in Shopify's <shopify-account> from its <template> (a tap also opens it). This keeps
 * the Shop sign-in iframe and its third-party cookies off every page load.
 */
const ACCOUNT_SCRIPT = 'https://cdn.shopify.com/storefront/web-components/account.js';

/** @param {Element | null} wrapper @param {boolean} open */
function activateAccount(wrapper, open) {
  if (!(wrapper instanceof HTMLElement) || wrapper.dataset.ready) return;
  const template = wrapper.querySelector('template');
  const trigger = wrapper.querySelector('[data-sky-account-trigger]');
  // Shopify renders a <shopify-store> next to <shopify-account>: move the whole template content
  const content = /** @type {DocumentFragment | undefined} */ (template?.content.cloneNode(true));
  const account = /** @type {HTMLElement | null | undefined} */ (content?.querySelector('shopify-account'));
  if (!content || !account || !trigger) return;
  wrapper.dataset.ready = 'true';
  trigger.replaceWith(content);
  template?.remove();

  if (!customElements.get('shopify-account')) import(ACCOUNT_SCRIPT).catch(() => {});
  if (open) {
    // the component renders its button asynchronously: wait for it (up to ~3s), then open the popover
    customElements.whenDefined('shopify-account').then(() => {
      const started = performance.now();
      const tryOpen = () => {
        const button = /** @type {HTMLElement | null} */ (account.shadowRoot?.querySelector('button'));
        if (button) button.click();
        else if (performance.now() - started < 3000) requestAnimationFrame(tryOpen);
      };
      tryOpen();
    });
  }
}

let lastPointerDown = 0;
/** @type {{ wrapper: Element, until: number } | null} */
let swallowClick = null;

document.addEventListener('pointerover', (event) => {
  const wrapper = event.target instanceof Element ? event.target.closest('[data-sky-account]') : null;
  if (wrapper && event.pointerType === 'mouse') activateAccount(wrapper, false);
});
// Touch / pen: build and open on pointerdown, and swallow the click that follows so it doesn't
// toggle the freshly opened popover shut
document.addEventListener(
  'pointerdown',
  (event) => {
    lastPointerDown = performance.now();
    if (event.pointerType === 'mouse') return;
    const wrapper = event.target instanceof Element ? event.target.closest('[data-sky-account]') : null;
    if (!(wrapper instanceof HTMLElement) || wrapper.dataset.ready) return;
    activateAccount(wrapper, true);
    swallowClick = { wrapper, until: performance.now() + 800 };
  },
  true
);
document.addEventListener(
  'click',
  (event) => {
    if (!swallowClick) return;
    const { wrapper, until } = swallowClick;
    swallowClick = null;
    if (performance.now() < until && event.target instanceof Node && wrapper.contains(event.target)) {
      event.preventDefault();
      event.stopPropagation();
    }
  },
  true
);
// Keyboard focus (not the focus that comes with a tap/click)
document.addEventListener('focusin', (event) => {
  if (performance.now() - lastPointerDown < 500) return;
  const wrapper = event.target instanceof Element ? event.target.closest('[data-sky-account]') : null;
  if (wrapper) activateAccount(wrapper, false);
});
document.addEventListener('click', (event) => {
  const trigger = event.target instanceof Element ? event.target.closest('[data-sky-account-trigger]') : null;
  if (!trigger) return;
  event.preventDefault();
  activateAccount(trigger.closest('[data-sky-account]'), true);
});
